import {Product} from "../model/lib/interfaces/Product";
import {OrderProduct} from "../model/lib/interfaces/OrderProduct";
import {Order} from "../model/lib/interfaces/Order";

export interface MenuOption {
  key: number;
  label: string;
  starterString: string;
  menuString: string;
  desertString: string;
  orderProducts: OrderProduct[];
}

export class MenuHelper {
  public static readonly STARTER_CATEGORY = 'Menü Vorspeisen';
  public static readonly MAIN_CATEGORIES = ['Menü', 'Menü Hauptspeisen'];
  public static readonly DESSERT_CATEGORY = 'Menü Nachspeisen';
  public static readonly NUMBERED_MENU_CATEGORY = /^Menü\s*(\d+)$/i;

  public static buildMenuOptions(products: Product[]): MenuOption[] {
    const valid: Product[] = (products || []).filter((p: Product) => !!p && !!p.category);

    const starter: Product | undefined = valid.find(p => MenuHelper.categoryName(p) === MenuHelper.STARTER_CATEGORY);
    const dessert: Product | undefined = valid.find(p => MenuHelper.categoryName(p) === MenuHelper.DESSERT_CATEGORY);
    const combinedMain: Product | undefined = valid.find(
      p => MenuHelper.MAIN_CATEGORIES.indexOf(MenuHelper.categoryName(p)) > -1
    );
    const numberedMains: Product[] = valid
      .filter(p => MenuHelper.isNumberedMenu(p))
      .sort((a: Product, b: Product) => {
        const diff = MenuHelper.menuNumber(a) - MenuHelper.menuNumber(b);
        return diff !== 0 ? diff : (a.order || 0) - (b.order || 0);
      });

    const mains: Product[] = [];
    if (combinedMain) mains.push(combinedMain);
    numberedMains.forEach(p => mains.push(p));

    if (!mains.length) {
      return (starter || dessert) ? [MenuHelper.createOption(undefined, starter, dessert)] : [];
    }

    return mains.map((main: Product) => MenuHelper.createOption(main, starter, dessert));
  }

  public static findOptionForOrder(options: MenuOption[], order: Order): MenuOption | undefined {
    if (!order || !order.orderProducts || !order.orderProducts.length || !options) return undefined;

    const productIds: number[] = order.orderProducts
      .filter((op: OrderProduct) => !!op && !!op.product)
      .map((op: OrderProduct) => op.product.id);

    return options.find((option: MenuOption) => option.key != null && productIds.indexOf(option.key) > -1);
  }

  public static findOptionByKey(options: MenuOption[], key: number): MenuOption | undefined {
    if (key == null || !options) return undefined;
    return options.find((option: MenuOption) => option.key === key);
  }

  private static createOption(main: Product | undefined, starter: Product | undefined, dessert: Product | undefined): MenuOption {
    const orderProducts: OrderProduct[] = [];

    if (starter && !!starter.name) {
      orderProducts.push({product: starter, quantity: 1} as OrderProduct);
    }
    if (main) {
      orderProducts.push({product: main, quantity: 1} as OrderProduct);
    }
    if (dessert && !!dessert.name) {
      orderProducts.push({product: dessert, quantity: 1} as OrderProduct);
    }

    const menuString: string = !!main ? `${main.name}` : '';

    return {
      key: !!main ? main.id : null,
      label: !!main && MenuHelper.isNumberedMenu(main)
        ? `${MenuHelper.categoryName(main)}: ${main.name}`
        : menuString,
      starterString: !!starter ? `${starter.name}` : '',
      menuString,
      desertString: !!dessert ? `${dessert.name}` : '',
      orderProducts,
    };
  }

  private static categoryName(product: Product): string {
    return !!product && !!product.category && !!product.category.name ? product.category.name.trim() : '';
  }

  private static isNumberedMenu(product: Product): boolean {
    return MenuHelper.NUMBERED_MENU_CATEGORY.test(MenuHelper.categoryName(product));
  }

  private static menuNumber(product: Product): number {
    const match = MenuHelper.categoryName(product).match(MenuHelper.NUMBERED_MENU_CATEGORY);
    return match ? parseInt(match[1], 10) : 0;
  }
}



// WEBPACK FOOTER //
// ./src/utils/menu-helper.ts